import { useState, useCallback } from "react";
import {
  useCustomMutation,
  useInvalidate,
  useNotification,
} from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../providers/dataProvider";
import type { ItemPendingActivityInfo } from "../types";

interface UseKanbanMutationOptions {
  resource: string;
  itemTypeName: string; // Aras ItemType name for promote API URL
}

interface PendingVoteState {
  itemId: string;
  targetState: string;
  activity: ItemPendingActivityInfo;
}

interface UseKanbanMutationResult {
  moveItem: (itemId: string, targetState: string) => Promise<void>;
  isMoving: boolean;
  /** Set when preflight detects a pending workflow activity requiring a vote */
  pendingVote: PendingVoteState | null;
  /** Submit the vote (called from KanbanVoteModal) */
  submitVote: (
    pathId: string,
    pathName: string,
    comments?: string,
  ) => Promise<void>;
  /** Dismiss the vote dialog without voting */
  clearPendingVote: () => void;
}

/**
 * Hook for Kanban drag-and-drop mutations.
 *
 * Flow:
 * 1. moveItem() calls GET /items/{type}/{id}/pending-activity (preflight)
 * 2. If 204 → promote directly (current behavior)
 * 3. If 200 → set pendingVote state → caller shows KanbanVoteModal
 * 4. submitVote() calls POST /workflow/complete → refetch
 */
export const useKanbanMutation = ({
  resource,
  itemTypeName,
}: UseKanbanMutationOptions): UseKanbanMutationResult => {
  const { t } = useTranslation();
  const { mutateAsync } = useCustomMutation();
  const invalidate = useInvalidate();
  const { open: notify } = useNotification();
  const [isMoving, setIsMoving] = useState(false);
  const [pendingVote, setPendingVote] = useState<PendingVoteState | null>(null);

  const refetchList = useCallback(() => {
    invalidate({ resource, invalidates: ["list"] });
  }, [invalidate, resource]);

  const moveItem = async (itemId: string, targetState: string) => {
    setIsMoving(true);
    try {
      // Preflight: check for pending workflow activity
      let pendingActivity: ItemPendingActivityInfo | null = null;
      try {
        const res = await axiosInstance.get(
          `items/${itemTypeName}/${itemId}/pending-activity`,
        );
        if (res.status === 200 && res.data) {
          pendingActivity = res.data as ItemPendingActivityInfo;
        }
      } catch {
        // 204 or error → fall through to promote
      }

      if (pendingActivity && pendingActivity.paths.length > 0) {
        // Workflow requires a vote — show dialog instead of promoting
        setPendingVote({ itemId, targetState, activity: pendingActivity });
        return;
      }

      // No pending activity → promote directly
      await mutateAsync({
        url: `/items/${itemTypeName}/${itemId}/promote`,
        method: "post",
        values: { targetState },
        successNotification: false,
        errorNotification: false,
      });

      notify?.({
        type: "success",
        message: t("kanban.promoteSuccess", { state: targetState }),
      });

      refetchList();
    } catch {
      notify?.({
        type: "error",
        message: t("kanban.promoteError"),
      });
      throw new Error("Promote failed");
    } finally {
      setIsMoving(false);
    }
  };

  const submitVote = async (
    pathId: string,
    pathName: string,
    comments?: string,
  ) => {
    if (!pendingVote) return;

    setIsMoving(true);
    try {
      await axiosInstance.post("workflow/complete", {
        activityId: pendingVote.activity.activityId,
        activityAssignmentId: pendingVote.activity.activityAssignmentId,
        pathId,
        pathName,
        comments: comments || null,
      });

      notify?.({
        type: "success",
        message: t("kanban.voteSuccess", { path: pathName }),
      });

      setPendingVote(null);

      // Aras may take a moment to process the workflow transition
      setTimeout(refetchList, 2000);
    } catch {
      notify?.({
        type: "error",
        message: t("kanban.voteError"),
      });
    } finally {
      setIsMoving(false);
    }
  };

  const clearPendingVote = useCallback(() => {
    setPendingVote(null);
  }, []);

  return { moveItem, isMoving, pendingVote, submitVote, clearPendingVote };
};
